import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from 'config/api';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { Company, CompanyDocument, SubscriptionPlan, SubscriptionStatus } from './companyTypes';
import { User } from 'features/projects/projectsTypes';

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Company', 'CompanyDocuments', 'Subscription'],
  endpoints: (builder) => ({
    // Company Info
    getCompanyDetails: builder.query<Company, string>({
      query: (companyId) => ({
        url: `${endpoints.companies.base}/${companyId}`,
        method: 'GET'
      }),
      providesTags: ['Company']
    }),

    getCompanySeats: builder.query<{
      company: { id: string; name: string };
      seats: { allowed: number; allocated: number; used: number; available: number };
      subscription: { plan: string; status: string };
    }, void>({
      query: () => ({
        url: `${endpoints.companies.base}/seats`,
        method: 'GET'
      }),
      providesTags: ['Company']
    }),

    // updateCompany: builder.mutation<Company, { companyId: string; data: Partial<Company> }>({
    //   query: ({ companyId, data }) => ({
    //     url: `${endpoints.companies.base}/${companyId}`,
    //     method: 'PUT',
    //     data,
    //   }),
    //   invalidatesTags: ['Company']
    // }),

    updateCompany: builder.mutation<Company, { companyId: string; data: Partial<Company> | FormData }>({
      query: ({ companyId, data }) => {
        const isFormData = data instanceof FormData;

        return {
          url: `${endpoints.companies.base}/${companyId}`,
          method: 'PUT',
          data,
          headers: isFormData ? undefined : { 'Content-Type': 'application/json' },
          // headers: isFormData
          //   ? { 'Content-Type': 'multipart/form-data' }
          //   : { 'Content-Type': 'application/json' },
        };
      },
      invalidatesTags: ['Company']
    }),


    // Documents
    getCompanyDocuments: builder.query<CompanyDocument[], string>({
      query: (companyId) => ({
        url: endpoints.companies.documents(companyId),
        method: 'GET',
      }),
      providesTags: ['CompanyDocuments']
    }),

    uploadCompanyDocument: builder.mutation<unknown, { companyId: string; formData: FormData }>({
      query: ({ companyId, formData }) => ({
        url: endpoints.companies.documents(companyId),
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      }),
      invalidatesTags: ['CompanyDocuments']
    }),

    deleteCompanyDocument: builder.mutation<unknown, { companyId: string; documentId: string }>({
      query: ({ companyId, documentId }) => ({
        url: endpoints.companies.documentDelete(companyId, documentId),
        method: 'DELETE',
      }),
      invalidatesTags: ['CompanyDocuments']
    }),

    updateCompanyDocument: builder.mutation<unknown, { companyId: string; documentId: string; data: { name?: string; description?: string } }>({
      query: ({ companyId, documentId, data }) => ({
        url: endpoints.companies.documentUrl(companyId, documentId),
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['CompanyDocuments']
    }),

    getCompanyDocumentUrl: builder.query<unknown, { companyId: string; documentId: string }>({
      query: ({ companyId, documentId }) => ({
        url: endpoints.companies.documentUrl(companyId, documentId),
        method: 'GET',
      }),
    }),

    getCompanyLogoUrl: builder.query<{ url: string }, string>({
      query: (companyId) => ({
        url: `${endpoints.companies.base}/${companyId}/logo`,
        method: 'GET',
      }),
    }),

    uploadCompanyLogo: builder.mutation<{ success: boolean; message: string; logo: Company['logo'] }, { companyId: string; file: File }>({
      query: ({ companyId, file }) => {
        const formData = new FormData();
        formData.append('logo', file);
        return {
          url: `${endpoints.companies.base}/${companyId}/logo`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: ['Company'],
    }),

    removeCompanyLogo: builder.mutation<{ success: boolean; message: string }, string>({
      query: (companyId) => ({
        url: `${endpoints.companies.base}/${companyId}/logo`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Company'],
    }),

    getAllCompanies: builder.query<{ companies: Company[]; users: User[] }, void>({
      query: () => ({
        url: endpoints.companies.base,
        method: 'GET',
      }),
    }),

    // Subscription endpoints
    getSubscriptionPlans: builder.query<{ plans: SubscriptionPlan[] }, void>({
      query: () => ({
        url: '/api/subscriptions/plans',
        method: 'GET',
      }),
    }),

    getSubscriptionStatus: builder.query<{ subscription: SubscriptionStatus }, string>({
      query: (companyId) => ({
        url: `/api/subscriptions/status/${companyId}`,
        method: 'GET',
      }),
      providesTags: ['Subscription'],
    }),

    createCheckoutSession: builder.mutation<
      { sessionId: string; url: string; seatCount: number; pricePerSeat: number; totalAmount: number }, 
      { priceId: string; companyId: string; allowedSeats: number }
    >({
      query: ({ priceId, companyId, allowedSeats }) => ({
        url: '/api/subscriptions/create-checkout-session',
        method: 'POST',
        data: { priceId, companyId, allowedSeats },
      }),
    }),

    cancelSubscription: builder.mutation<{ message: string }, { companyId: string; cancelAtPeriodEnd?: boolean }>({
      query: ({ companyId, cancelAtPeriodEnd = true }) => ({
        url: `/api/subscriptions/cancel/${companyId}`,
        method: 'POST',
        data: { cancelAtPeriodEnd },
      }),
      invalidatesTags: ['Subscription', 'Company'],
    }),

    reactivateSubscription: builder.mutation<{ message: string }, string>({
      query: (companyId) => ({
        url: `/api/subscriptions/reactivate/${companyId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Subscription', 'Company'],
    }),


    updateAllowedSeats: builder.mutation<{ message: string; subscription: SubscriptionStatus }, { companyId: string; allowedSeats: number }>({
      query: ({ companyId, allowedSeats }) => ({
        url: `/api/subscriptions/allowed-seats/${companyId}`,
        method: 'PUT',
        data: { allowedSeats },
      }),
      invalidatesTags: ['Subscription', 'Company'],
    }),

    // Company Profile Subscription Management
    getMyCompanySubscriptionStatus: builder.query<{ 
      subscription: SubscriptionStatus; 
      company: { id: string; name: string; isOwner: boolean } 
    }, void>({
      query: () => ({
        url: '/api/subscriptions/my-subscription-status',
        method: 'GET',
      }),
      providesTags: ['Subscription'],
    }),

    cancelMyCompanySubscription: builder.mutation<{ 
      message: string; 
      subscription: { 
        status: string; 
        cancelAtPeriodEnd: boolean; 
        currentPeriodEnd: string | null; 
        plan: string 
      } 
    }, { cancelAtPeriodEnd?: boolean }>({
      query: ({ cancelAtPeriodEnd = true }) => ({
        url: '/api/subscriptions/cancel-my-subscription',
        method: 'POST',
        data: { cancelAtPeriodEnd },
      }),
      invalidatesTags: ['Subscription', 'Company'],
    }),

    reactivateMyCompanySubscription: builder.mutation<{ 
      message: string; 
      subscription: { 
        status: string; 
        cancelAtPeriodEnd: boolean; 
        currentPeriodEnd: string | null; 
        plan: string 
      } 
    }, void>({
      query: () => ({
        url: '/api/subscriptions/reactivate-my-subscription',
        method: 'POST',
      }),
      invalidatesTags: ['Subscription', 'Company'],
    }),

    createCustomerPortalSession: builder.mutation<{ url: string }, void>({
      query: () => ({
        url: '/api/subscriptions/create-customer-portal',
        method: 'POST',
      }),
    }),
  })
});

export const {
  useGetCompanyDetailsQuery,
  useGetCompanySeatsQuery,
  useUpdateCompanyMutation,
  useGetCompanyDocumentsQuery,
  useUploadCompanyDocumentMutation,
  useDeleteCompanyDocumentMutation,
  useUpdateCompanyDocumentMutation,
  useLazyGetCompanyDocumentUrlQuery,
  useGetCompanyLogoUrlQuery,
  useUploadCompanyLogoMutation,
  useRemoveCompanyLogoMutation,
  useGetAllCompaniesQuery,
  useGetSubscriptionPlansQuery,
  useGetSubscriptionStatusQuery,
  useCreateCheckoutSessionMutation,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useUpdateAllowedSeatsMutation,
  // Company Profile Subscription Management
  useGetMyCompanySubscriptionStatusQuery,
  useCancelMyCompanySubscriptionMutation,
  useReactivateMyCompanySubscriptionMutation,
  useCreateCustomerPortalSessionMutation,
} = companyApi;
